import React, { useCallback, useMemo, useState } from 'react';
import {
	Avatar,
	Badge,
	Button,
	Input,
	Popconfirm,
	Table,
	Tag,
	Tooltip,
	Typography,
	message,
} from 'antd';
import type {
	ColumnsType,
	FilterValue,
	SorterResult,
	TablePaginationConfig,
} from 'antd/es/table/interface';
import {
	CheckCircleFilled,
	ClockCircleOutlined,
	CommentOutlined,
	DeleteOutlined,
	EditOutlined,
	FileTextOutlined,
	LinkOutlined,
	PlusOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Link, useSearchParams } from 'react-router-dom';
import ContentModal from './ContentModal';
import FeedbackFormModal from './FeedbackFormModal';
import IssueFormModal from './IssueFormModal';
import LinkedLineItemsModal from './LinkedLineItemsModal';
import ProgrammingStatsFormModal from './ProgrammingStatsFormModal';
import ProgramFeedbackModal from './ProgramFeedbackModal';
import ProgramIssuesModal from './ProgramIssuesModal';
import ProgramReviewsModal from './ProgramReviewsModal';
import ReviewFormModal from './ReviewFormModal';
import { authorName, isReviewAuthor, programPartLabel } from './authors';
import { hasRichTextContent, formatOrderLineItemSubtitle } from './fields';
import {
	formatProgrammingCaption,
	type ProgrammingStatus,
	programmingStatus,
} from './programmingStatus';
import {
	PROGRESS_FILTERS,
	PROGRESS_GATES,
	parseProgrammingStatusesParam,
	parseProgressFilterValues,
	progressFilterValues,
} from './progressFilters';
import { formatDurationCaption } from './sessionLogging';
import {
	useDeleteProgramReviewMutation,
	useEnsureProgramMutation,
	useGetProgramScoringGridFilterUsersQuery,
	useGetProgramScoringGridQuery,
	useLazyGetProgramReviewQuery,
} from '../../redux/api/manufacturing';
import { useGetLoggedInUserQuery } from '../../redux/api/users';
import type {
	ProgramReview,
	ProgramScoringRow,
	ProgramType,
	ProgramUser,
} from '../../interfaces/CamProgramming.interface';
import {
	programTypeAllowsMultiplePrograms,
	programTypeUsesFilename,
} from '../../interfaces/CamProgramming.interface';

const { Text } = Typography;

const SECONDARY_REVIEW_BLOCKED_TOOLTIP =
	'Log the first review before adding a secondary review.';

type ProgressStepStatus = 'wait' | 'process' | 'finish';

const PROGRESS_STEP_LABELS = [
	'Programming',
	'First review',
	'Secondary review',
] as const;

const progressStepStatus = (
	record: ProgramScoringRow,
	step: 'programming' | 'first_review' | 'secondary_review'
): ProgressStepStatus => {
	if (step === 'programming') {
		switch (programmingStatus(record)) {
			case 'done':
				return 'finish';
			case 'in_progress':
				return 'process';
			default:
				return 'wait';
		}
	}

	if (step === 'first_review') {
		if (record.first_review_completed_at) {
			return 'finish';
		}
		if (record.first_review_id || record.reviews_count > 0) {
			return 'process';
		}
		return 'wait';
	}

	if (record.secondary_review_completed_at) {
		return 'finish';
	}
	if (record.secondary_review_id || record.reviews_count >= 2) {
		return 'process';
	}
	return 'wait';
};

const progressStepTooltip = (index: number, status: ProgressStepStatus) => {
	const label = PROGRESS_STEP_LABELS[index];
	if (status === 'finish') {
		return `${label}: Done`;
	}
	if (status === 'process') {
		return `${label}: In progress`;
	}
	return `${label}: Pending`;
};

const ProgramProgressSteps: React.FC<{ record: ProgramScoringRow }> = ({
	record,
}) => {
	const statuses: ProgressStepStatus[] = [
		progressStepStatus(record, 'programming'),
		progressStepStatus(record, 'first_review'),
		progressStepStatus(record, 'secondary_review'),
	];

	return (
		<div
			className="cam-grid-progress-track"
			role="group"
			aria-label="Program progress"
		>
			{statuses.map((status, index) => {
				const label = progressStepTooltip(index, status);
				return (
					<React.Fragment key={PROGRESS_STEP_LABELS[index]}>
						{index > 0 && (
							<span
								className={`cam-grid-progress-track__line${statuses[index - 1] === 'finish'
									? ' cam-grid-progress-track__line--done'
									: ''
									}`}
								aria-hidden
							/>
						)}
						<Tooltip title={label}>
							<span
								className={`cam-grid-progress-track__node cam-grid-progress-track__node--${status}`}
								role="img"
								aria-label={label}
							>
								{status === 'finish' && <CheckCircleFilled aria-hidden />}
							</span>
						</Tooltip>
					</React.Fragment>
				);
			})}
		</div>
	);
};

const COUNT_FILTERS = [
	{ text: 'Has entries', value: 'yes' },
	{ text: 'None', value: 'no' },
];

const ORDERING_KEYS: Record<string, string> = {
	part: 'order_line_item__name',
	prog_status: 'programming_started_at',
	review_status: 'last_review_completed_at',
	programmer: 'programmer__last_name',
	first_reviewer: 'first_reviewer_last_name',
	secondary_reviewer: 'secondary_reviewer_last_name',
	issues: 'issues_count',
	feedback: 'feedback_count',
};

type ProgramTableQuery = {
	page: number;
	search: string;
	ordering?: string;
	part_name?: string;
	programming_status?: ProgrammingStatus[];
	/** @deprecated Use `programming_status` instead. */
	programming_done?: boolean;
	first_review_status?: ProgrammingStatus[];
	secondary_review_status?: ProgrammingStatus[];
	/** @deprecated Use the per-gate review statuses instead. */
	reviewed?: boolean;
	programmer?: string;
	first_reviewer?: string;
	secondary_reviewer?: string;
	no_first_review?: boolean;
	no_secondary_review?: boolean;
	has_issues?: boolean;
	has_feedback?: boolean;
};

const parseBooleanParam = (value: string | null): boolean | undefined => {
	if (value === 'true') {
		return true;
	}
	if (value === 'false') {
		return false;
	}
	return undefined;
};

/**
 * The URL is the source of truth for the table state so a reload (or a shared
 * link) restores the same page, search, sort and filters.
 */
const queryFromSearchParams = (params: URLSearchParams): ProgramTableQuery => {
	const pageValue = Number(params.get('page'));
	const programmingStatus = parseProgrammingStatusesParam(
		params.get('programming_status')
	);
	const legacyProgrammingDone = parseBooleanParam(params.get('programming_done'));

	return {
		page:
			Number.isFinite(pageValue) && pageValue > 1 ? Math.floor(pageValue) : 1,
		search: params.get('search') ?? '',
		ordering: params.get('ordering') ?? undefined,
		part_name: params.get('part_name') ?? undefined,
		programming_status:
			programmingStatus ??
			(legacyProgrammingDone === true ? ['done'] : undefined),
		programming_done: legacyProgrammingDone,
		first_review_status: parseProgrammingStatusesParam(
			params.get('first_review_status')
		),
		secondary_review_status: parseProgrammingStatusesParam(
			params.get('secondary_review_status')
		),
		reviewed: parseBooleanParam(params.get('reviewed')),
		programmer: params.get('programmer') ?? undefined,
		first_reviewer: params.get('first_reviewer') ?? undefined,
		secondary_reviewer: params.get('secondary_reviewer') ?? undefined,
		no_first_review: params.get('no_first_review') === 'true' || undefined,
		no_secondary_review:
			params.get('no_secondary_review') === 'true' || undefined,
		has_issues: parseBooleanParam(params.get('has_issues')),
		has_feedback: parseBooleanParam(params.get('has_feedback')),
	};
};

const searchParamsFromQuery = (
	query: ProgramTableQuery,
	tab: string | null
): URLSearchParams => {
	const params = new URLSearchParams();
	if (tab) {
		params.set('tab', tab);
	}
	if (query.page > 1) {
		params.set('page', String(query.page));
	}
	if (query.search) {
		params.set('search', query.search);
	}
	if (query.ordering) {
		params.set('ordering', query.ordering);
	}
	if (query.part_name) {
		params.set('part_name', query.part_name);
	}
	PROGRESS_GATES.forEach(({ field }) => {
		const statuses = query[field];
		if (statuses?.length) {
			params.set(field, statuses.join(','));
		}
	});
	if (query.reviewed !== undefined) {
		params.set('reviewed', String(query.reviewed));
	}
	if (query.programmer) {
		params.set('programmer', query.programmer);
	}
	if (query.first_reviewer) {
		params.set('first_reviewer', query.first_reviewer);
	}
	if (query.secondary_reviewer) {
		params.set('secondary_reviewer', query.secondary_reviewer);
	}
	if (query.no_first_review) {
		params.set('no_first_review', 'true');
	}
	if (query.no_secondary_review) {
		params.set('no_secondary_review', 'true');
	}
	if (query.has_issues !== undefined) {
		params.set('has_issues', String(query.has_issues));
	}
	if (query.has_feedback !== undefined) {
		params.set('has_feedback', String(query.has_feedback));
	}
	return params;
};

export interface ProgramScoringTableProps {
	programType: ProgramType;
	/** Toolbar search placeholder — defaults from program type when omitted. */
	searchPlaceholder?: string;
}

export const PROGRAM_SCORING_SEARCH_PLACEHOLDERS: Record<ProgramType, string> = {
	cam: 'Search CAM programs...',
	keyence: 'Search Keyence programs...',
	inspection_plan: 'Search inspection plans...',
};

const getSortOrder = (
	columnKey: string,
	ordering?: string
): 'ascend' | 'descend' | undefined => {
	if (!ordering) {
		return undefined;
	}
	const field = ORDERING_KEYS[columnKey];
	if (!field) {
		return undefined;
	}
	if (ordering === field) {
		return 'ascend';
	}
	if (ordering === `-${field}`) {
		return 'descend';
	}
	return undefined;
};

const buildUserFilters = (users: ProgramUser[]) => [
	{ text: 'No review', value: 'none' },
	...users.map((user) => ({
		text: user.full_name || user.email,
		value: user.id,
	})),
];

const LogReviewButton: React.FC<{
	onClick: () => void;
	disabled?: boolean;
	disabledReason?: string;
}> = ({ onClick, disabled, disabledReason }) => {
	const button = (
		<Button
			size="small"
			icon={<PlusOutlined />}
			disabled={disabled}
			onClick={onClick}
		>
			Log
		</Button>
	);

	if (disabled && disabledReason) {
		return (
			<Tooltip title={disabledReason}>
				<span className="cam-grid-log-review">{button}</span>
			</Tooltip>
		);
	}

	return button;
};

const formatReviewCaption = (
	durationMinutes: number | null,
	completedAt: string | null,
	reviewExists: boolean
) => {
	const status = completedAt
		? 'done'
		: reviewExists
			? 'in_progress'
			: 'pending';
	return formatDurationCaption(status, durationMinutes);
};

const StatsCaption: React.FC<{ caption: string }> = ({ caption }) => (
	<Text ellipsis className="cam-entry__chip" style={{ fontSize: 12 }}>
		<ClockCircleOutlined /> {caption}
	</Text>
);

const formatProgrammingCaptionForRow = (record: ProgramScoringRow) =>
	formatProgrammingCaption(record);

const hasProgrammingStats = (record: ProgramScoringRow) =>
	programmingStatus(record) !== 'pending' ||
	(record.programming_duration_minutes != null &&
		record.programming_duration_minutes > 0);

const ProgrammerCell: React.FC<{
	record: ProgramScoringRow;
	onLogProgrammingStats: () => void;
	onViewProgrammerNotes: () => void;
	onLogAdditionalProgram?: () => void;
	allowAdditionalPrograms?: boolean;
}> = ({
	record,
	onLogProgrammingStats,
	onViewProgrammerNotes,
	onLogAdditionalProgram,
	allowAdditionalPrograms = false,
}) => {
		const caption = formatProgrammingCaptionForRow(record);
		const hasStats = hasProgrammingStats(record);

		if (!hasStats) {
			return <LogReviewButton onClick={onLogProgrammingStats} />;
		}

		return (
			<div className="cam-grid-review-slot">
				<div className="cam-grid-programmer">
					<Text ellipsis style={{ fontSize: 13 }}>
						{authorName(record.programmer, 'Unassigned')}
					</Text>
					{caption ? (
						<StatsCaption caption={caption} />
					) : (
						<Text style={{ fontSize: 12 }}>No stats logged</Text>
					)}
				</div>
				<div className="cam-grid-programmer-actions">
					{hasRichTextContent(record.programmer_feedback) && (
						<Tooltip title="View programmer notes">
							<button
								type="button"
								className="cam-tag-button"
								onClick={onViewProgrammerNotes}
							>
								<Tag icon={<FileTextOutlined />} color="purple" style={{ margin: 0 }}>
									Notes
								</Tag>
							</button>
						</Tooltip>
					)}
					<Tooltip title="Edit programming stats">
						<Button
							type="text"
							size="small"
							icon={<EditOutlined />}
							onClick={onLogProgrammingStats}
						/>
					</Tooltip>
					{allowAdditionalPrograms && onLogAdditionalProgram && (
						<Tooltip title="Log another program for this part">
							<Button
								type="text"
								size="small"
								icon={<PlusOutlined />}
								onClick={onLogAdditionalProgram}
							/>
						</Tooltip>
					)}
				</div>
			</div>
		);
	};

const ReviewSlotCell: React.FC<{
	reviewer: ProgramUser | null;
	reviewId: string | null;
	reviewExists: boolean;
	durationMinutes: number | null;
	completedAt: string | null;
	onLogReview: () => void;
	onViewReview: () => void;
	onEditReview?: () => void;
	onDeleteReview?: () => void;
	canEdit?: boolean;
	canDelete?: boolean;
	logDisabled?: boolean;
	logDisabledReason?: string;
	extra?: React.ReactNode;
}> = ({
	reviewer,
	reviewId,
	reviewExists,
	durationMinutes,
	completedAt,
	onLogReview,
	onViewReview,
	onEditReview,
	onDeleteReview,
	canEdit = false,
	canDelete = false,
	logDisabled,
	logDisabledReason,
	extra,
}) => {
		const caption = formatReviewCaption(durationMinutes, completedAt, reviewExists);

		const editButton =
			canEdit && onEditReview ? (
				<Tooltip title="Edit review">
					<Button
						type="text"
						size="small"
						icon={<EditOutlined />}
						onClick={(event) => {
							event.stopPropagation();
							onEditReview();
						}}
					/>
				</Tooltip>
			) : null;

		const deleteButton =
			canDelete && onDeleteReview ? (
				<Popconfirm
					title="Remove this review?"
					okText="Remove"
					okButtonProps={{ danger: true }}
					onConfirm={onDeleteReview}
				>
					<Tooltip title="Remove review">
						<Button
							type="text"
							size="small"
							danger
							icon={<DeleteOutlined />}
							onClick={(event) => event.stopPropagation()}
						/>
					</Tooltip>
				</Popconfirm>
			) : null;

		const actionButtons =
			editButton || deleteButton ? (
				<div className="cam-grid-programmer-actions">
					{editButton}
					{deleteButton}
				</div>
			) : null;

		const reviewContent = (
			<div className="cam-grid-programmer">
				<div className="cam-grid-reviewer-line">
					<Text ellipsis style={{ fontSize: 13 }}>
						{authorName(reviewer, 'Unassigned')}
					</Text>
					{extra}
				</div>
				{caption ? (
					<StatsCaption caption={caption} />
				) : (
					<Text style={{ fontSize: 12 }}>No stats logged</Text>
				)}
			</div>
		);

		if (reviewExists && reviewId) {
			return (
				<div className="cam-grid-review-slot">
					<Tooltip title="View review">
						<button
							type="button"
							className="cam-grid-review-slot__button cam-grid-review-slot--clickable"
							onClick={onViewReview}
						>
							{reviewContent}
						</button>
					</Tooltip>
					{actionButtons}
				</div>
			);
		}

		if (reviewExists) {
			return (
				<div className="cam-grid-review-slot">
					{reviewContent}
					{actionButtons}
				</div>
			);
		}

		return (
			<LogReviewButton
				onClick={onLogReview}
				disabled={logDisabled}
				disabledReason={logDisabledReason}
			/>
		);
	};

/**
 * Marks a row whose program covers more than one line item.
 *
 * Without it a shared program is indistinguishable from an ordinary one, and
 * the parts it covers look unaccounted for — they have no row of their own.
 * Rendered outside the part Link so clicking it opens the manager instead of
 * navigating to the part.
 */
const LinkedLineItemsIndicator: React.FC<{
	record: ProgramScoringRow;
	onClick: () => void;
}> = ({ record, onClick }) => {
	if (!record.id || record.linked_line_item_count <= 1) {
		return null;
	}

	const others = record.order_line_items
		.slice(1)
		.map((lineItem) => lineItem.name || 'Unnamed part');

	return (
		<Tooltip
			title={`Also covers ${others.join(', ')}. Click to manage.`}
		>
			<Button
				type="text"
				size="small"
				className="cam-grid-part-link-badge"
				icon={<LinkOutlined />}
				onClick={onClick}
			>
				{record.linked_line_item_count}
			</Button>
		</Tooltip>
	);
};

interface GridColumnHandlers {
	onViewIssues: (record: ProgramScoringRow) => void;
	onViewReviews: (record: ProgramScoringRow) => void;
	onLogReview: (record: ProgramScoringRow) => void;
	onViewReview: (record: ProgramScoringRow, reviewId: string) => void;
	onDeleteReview: (record: ProgramScoringRow, reviewId: string) => void;
	onLogIssue: (record: ProgramScoringRow) => void;
	onLogFeedback: (record: ProgramScoringRow) => void;
	onViewFeedback: (record: ProgramScoringRow) => void;
	onLogProgrammingStats: (record: ProgramScoringRow) => void;
	onViewProgrammerNotes: (record: ProgramScoringRow) => void;
	onLogAdditionalProgram: (record: ProgramScoringRow) => void;
	onManageLinkedLineItems: (record: ProgramScoringRow) => void;
}

interface BuildColumnsOptions extends GridColumnHandlers {
	programType: ProgramType;
	query: ProgramTableQuery;
	programmers: ProgramUser[];
	firstReviewers: ProgramUser[];
	secondaryReviewers: ProgramUser[];
	currentUserId?: string;
}

const buildColumns = ({
	programType,
	query,
	programmers,
	firstReviewers,
	secondaryReviewers,
	onViewIssues,
	onViewReviews,
	onLogReview,
	onViewReview,
	onDeleteReview,
	onLogIssue,
	onLogFeedback,
	onViewFeedback,
	onLogProgrammingStats,
	onViewProgrammerNotes,
	onLogAdditionalProgram,
	onManageLinkedLineItems,
	currentUserId,
}: BuildColumnsOptions): ColumnsType<ProgramScoringRow> => {
	const firstReviewerFilters = buildUserFilters(firstReviewers);
	const secondaryReviewerFilters = buildUserFilters(secondaryReviewers);

	const firstReviewFilterValue = query.no_first_review
		? (['none'] as FilterValue)
		: query.first_reviewer
			? ([query.first_reviewer] as FilterValue)
			: null;

	const secondaryReviewFilterValue = query.no_secondary_review
		? (['none'] as FilterValue)
		: query.secondary_reviewer
			? ([query.secondary_reviewer] as FilterValue)
			: null;

	return [
		{
			title: 'Part',
			key: 'part',
			fixed: 'left',
			width: programTypeUsesFilename(programType) ? 260 : 300,
			ellipsis: true,
			sorter: true,
			sortOrder: getSortOrder('part', query.ordering),
			filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
				<div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
					<Input
						placeholder="Part name"
						value={(selectedKeys[0] as string) || ''}
						onChange={(e) =>
							setSelectedKeys(e.target.value ? [e.target.value] : [])
						}
						onPressEnter={() => confirm()}
						style={{ marginBottom: 8, display: 'block' }}
					/>
					<Button type="primary" size="small" onClick={() => confirm()}>
						Filter
					</Button>
					<Button
						size="small"
						style={{ marginLeft: 8 }}
						onClick={() => {
							clearFilters?.();
							confirm();
						}}
					>
						Reset
					</Button>
				</div>
			),
			filteredValue: query.part_name ? [query.part_name] : null,
			render: (_: unknown, record: ProgramScoringRow) => (
				<div className="cam-grid-part-cell">
					<Link
						to={`/orders/line-items/${record.order_line_item.id}`}
						className="cam-grid-part-link"
					>
						<div className="cam-grid-part">
							{record.order_line_item.thumbnail_url ? (
								<Avatar
									shape="square"
									size={28}
									src={record.order_line_item.thumbnail_url}
								/>
							) : (
								<Avatar
									shape="square"
									size={28}
									style={{
										backgroundColor: '#f0f0f0',
										color: '#999',
										fontSize: 10,
									}}
								>
									3D
								</Avatar>
							)}
							<div className="cam-grid-part__info">
								<Text ellipsis style={{ fontSize: 13 }}>
									{record.order_line_item.name || '—'}
								</Text>
								<Text ellipsis type="secondary" style={{ fontSize: 11 }}>
									{formatOrderLineItemSubtitle(record.order_line_item)}
								</Text>
							</div>
						</div>
					</Link>
					<LinkedLineItemsIndicator
						record={record}
						onClick={() => onManageLinkedLineItems(record)}
					/>
				</div>
			),
		},
		...(programTypeUsesFilename(programType)
			? [
				{
					title: 'Filename',
					key: 'filename',
					width: 180,
					ellipsis: true,
					render: (_: unknown, record: ProgramScoringRow) => (
						<Text ellipsis style={{ fontSize: 13 }}>
							{record.filename?.trim() ||
								record.order_line_item.name ||
								'—'}
						</Text>
					),
				} satisfies ColumnsType<ProgramScoringRow>[number],
			]
			: []),
		{
			title: 'Progress',
			key: 'progress',
			width: 120,
			align: 'center',
			filters: PROGRESS_FILTERS,
			filterMultiple: true,
			filteredValue: progressFilterValues(query),
			render: (_: unknown, record: ProgramScoringRow) => (
				<ProgramProgressSteps record={record} />
			),
		},
		{
			title: 'Programmer',
			key: 'programmer',
			width: 260,
			sorter: true,
			sortOrder: getSortOrder('programmer', query.ordering),
			filters: programmers.map((user) => ({
				text: user.full_name || user.email,
				value: user.id,
			})),
			filteredValue: query.programmer ? [query.programmer] : null,
			render: (_: unknown, record: ProgramScoringRow) => (
				<ProgrammerCell
					record={record}
					onLogProgrammingStats={() => onLogProgrammingStats(record)}
					onViewProgrammerNotes={() => onViewProgrammerNotes(record)}
					onLogAdditionalProgram={() => onLogAdditionalProgram(record)}
					allowAdditionalPrograms={programTypeAllowsMultiplePrograms(programType)}
				/>
			),
		},
		{
			title: 'Review',
			key: 'first_reviewer',
			width: 220,
			sorter: true,
			sortOrder: getSortOrder('first_reviewer', query.ordering),
			filters: firstReviewerFilters,
			filteredValue: firstReviewFilterValue,
			render: (_: unknown, record: ProgramScoringRow) => {
				const isOwner =
					!!record.first_review_id &&
					currentUserId === record.first_reviewer?.id;

				return (
					<ReviewSlotCell
						reviewer={record.first_reviewer}
						reviewId={record.first_review_id}
						reviewExists={record.reviews_count > 0}
						durationMinutes={record.first_review_duration_minutes}
						completedAt={record.first_review_completed_at}
						onLogReview={() => onLogReview(record)}
						onViewReview={() =>
							onViewReview(record, record.first_review_id as string)
						}
						canEdit={isOwner}
						onEditReview={() =>
							onViewReview(record, record.first_review_id as string)
						}
						canDelete={isOwner}
						onDeleteReview={() =>
							onDeleteReview(record, record.first_review_id as string)
						}
					/>
				);
			},
		},
		{
			title: 'Secondary Review',
			key: 'secondary_reviewer',
			width: 240,
			sorter: true,
			sortOrder: getSortOrder('secondary_reviewer', query.ordering),
			filters: secondaryReviewerFilters,
			filteredValue: secondaryReviewFilterValue,
			render: (_: unknown, record: ProgramScoringRow) => {
				const extraReviews = Math.max(0, record.reviews_count - 2);
				const extra =
					record.reviews_count >= 2 && extraReviews > 0 ? (
						<Tooltip title="View additional reviews">
							<span
								role="button"
								tabIndex={0}
								className="cam-tag-button cam-grid-extra-reviews"
								onClick={(event) => {
									event.stopPropagation();
									onViewReviews(record);
								}}
								onKeyDown={(event) => {
									if (event.key === 'Enter' || event.key === ' ') {
										event.preventDefault();
										event.stopPropagation();
										onViewReviews(record);
									}
								}}
							>
								<Badge
									count={`+${extraReviews}`}
									size="small"
									color="#1677ff"
									className="cam-grid-extra-reviews-badge"
								/>
							</span>
						</Tooltip>
					) : null;

				const isOwner =
					!!record.secondary_review_id &&
					currentUserId === record.secondary_reviewer?.id;

				return (
					<ReviewSlotCell
						reviewer={record.secondary_reviewer}
						reviewId={record.secondary_review_id}
						reviewExists={record.reviews_count >= 2}
						durationMinutes={record.secondary_review_duration_minutes}
						completedAt={record.secondary_review_completed_at}
						onLogReview={() => onLogReview(record)}
						onViewReview={() =>
							onViewReview(record, record.secondary_review_id as string)
						}
						canEdit={isOwner}
						onEditReview={() =>
							onViewReview(record, record.secondary_review_id as string)
						}
						canDelete={isOwner}
						onDeleteReview={() =>
							onDeleteReview(record, record.secondary_review_id as string)
						}
						logDisabled={record.reviews_count === 0}
						logDisabledReason={SECONDARY_REVIEW_BLOCKED_TOOLTIP}
						extra={extra}
					/>
				);
			},
		},
		{
			title: 'Issues',
			key: 'issues',
			width: 180,
			align: 'center',
			sorter: true,
			sortOrder: getSortOrder('issues', query.ordering),
			filters: COUNT_FILTERS,
			filteredValue:
				query.has_issues === true
					? ['yes']
					: query.has_issues === false
						? ['no']
						: null,
			render: (_: unknown, record: ProgramScoringRow) => (
				<div className="cam-grid-issues-cell">
					{record.issues_count > 0 ? (
						<Tooltip title="View reported issues">
							<button
								type="button"
								className="cam-tag-button"
								onClick={() => onViewIssues(record)}
							>
								<Tag color="warning" style={{ margin: 0 }}>
									{record.issues_count}
								</Tag>
							</button>
						</Tooltip>
					) : (
						<Tag style={{ margin: 0 }}>0</Tag>
					)}
					<Button
						size="small"
						icon={<PlusOutlined />}
						onClick={() => onLogIssue(record)}
					>
						Log
					</Button>
				</div>
			),
		},
		{
			title: 'Feedback',
			key: 'feedback',
			width: 150,
			align: 'center',
			sorter: true,
			sortOrder: getSortOrder('feedback', query.ordering),
			filters: COUNT_FILTERS,
			filteredValue:
				query.has_feedback === true
					? ['yes']
					: query.has_feedback === false
						? ['no']
						: null,
			render: (_: unknown, record: ProgramScoringRow) => (
				<div className="cam-grid-issues-cell">
					{record.feedback_count > 0 ? (
						<Tooltip title="View feedback">
							<button
								type="button"
								className="cam-tag-button"
								onClick={() => onViewFeedback(record)}
							>
								<Tag icon={<CommentOutlined />} color="geekblue" style={{ margin: 0 }}>
									{record.feedback_count}
								</Tag>
							</button>
						</Tooltip>
					) : (
						<Tag style={{ margin: 0 }}>0</Tag>
					)}
					<Button
						size="small"
						icon={<PlusOutlined />}
						onClick={() => onLogFeedback(record)}
					>
						Log
					</Button>
				</div>
			),
		},
	];
};

const parseCountFilter = (value: FilterValue | null | undefined) => {
	if (!value?.length) {
		return undefined;
	}
	if (value[0] === 'yes') {
		return true;
	}
	if (value[0] === 'no') {
		return false;
	}
	return undefined;
};

const parseReviewerFilter = (value: FilterValue | null | undefined) => {
	if (!value?.length) {
		return { reviewer: undefined, none: undefined };
	}
	if (value[0] === 'none') {
		return { reviewer: undefined, none: true };
	}
	return { reviewer: String(value[0]), none: undefined };
};

/**
 * Read-only program scoring grid for one program type. Each tab on the
 * Program Scoring page mounts its own instance, filtered by `programType`.
 */
const ProgramScoringTable: React.FC<ProgramScoringTableProps> = ({
	programType,
	searchPlaceholder,
}) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const query = useMemo(
		() => queryFromSearchParams(searchParams),
		[searchParams]
	);

	const updateQuery = useCallback(
		(updates: Partial<ProgramTableQuery>) => {
			setSearchParams(
				(current) =>
					searchParamsFromQuery(
						{ ...queryFromSearchParams(current), ...updates },
						current.get('tab')
					),
				{ replace: true }
			);
		},
		[setSearchParams]
	);
	const [issuesModal, setIssuesModal] = useState<{
		programId: string;
		partName: string;
	} | null>(null);
	const [feedbackModal, setFeedbackModal] = useState<{
		programId: string;
		partName: string;
	} | null>(null);
	const [reviewsModal, setReviewsModal] = useState<{
		programId: string;
		partName: string;
	} | null>(null);
	const [issueModalProgramId, setIssueModalProgramId] = useState<
		string | null
	>(null);
	const [feedbackModalProgramId, setFeedbackModalProgramId] = useState<
		string | null
	>(null);
	const [programmingStatsModal, setProgrammingStatsModal] =
		useState<ProgramScoringRow | null>(null);
	const [addProgramModal, setAddProgramModal] = useState<{
		orderLineItemId: string;
		orderLineItemName: string | null;
		existingPrograms: { id: string; filename: string }[];
	} | null>(null);
	const [reviewFormModal, setReviewFormModal] = useState<{
		programId: string;
		review: ProgramReview | null;
	} | null>(null);
	const [viewingReview, setViewingReview] = useState<ProgramReview | null>(
		null
	);
	const [viewingProgrammerNotes, setViewingProgrammerNotes] =
		useState<ProgramScoringRow | null>(null);
	const [linkedLineItemsModal, setLinkedLineItemsModal] =
		useState<ProgramScoringRow | null>(null);

	const { data: currentUser } = useGetLoggedInUserQuery();
	const { data: filterUsers } = useGetProgramScoringGridFilterUsersQuery({
		program_type: programType,
	});
	const [fetchReview] = useLazyGetProgramReviewQuery();
	const [ensureProgram] = useEnsureProgramMutation();
	const [deleteReview] = useDeleteProgramReviewMutation();
	const { data, isLoading, isFetching } = useGetProgramScoringGridQuery({
		program_type: programType,
		page: query.page,
		...(query.search ? { search: query.search } : {}),
		...(query.ordering ? { ordering: query.ordering } : {}),
		...(query.part_name ? { part_name: query.part_name } : {}),
		...(query.programming_status?.length
			? { programming_status: query.programming_status.join(',') }
			: query.programming_done !== undefined
				? { programming_done: query.programming_done }
				: {}),
		...(query.first_review_status?.length
			? { first_review_status: query.first_review_status.join(',') }
			: {}),
		...(query.secondary_review_status?.length
			? { secondary_review_status: query.secondary_review_status.join(',') }
			: {}),
		...(query.reviewed !== undefined ? { reviewed: query.reviewed } : {}),
		...(query.programmer ? { programmer: query.programmer } : {}),
		...(query.first_reviewer ? { first_reviewer: query.first_reviewer } : {}),
		...(query.secondary_reviewer
			? { secondary_reviewer: query.secondary_reviewer }
			: {}),
		...(query.no_first_review ? { no_first_review: query.no_first_review } : {}),
		...(query.no_secondary_review
			? { no_secondary_review: query.no_secondary_review }
			: {}),
		...(query.has_issues !== undefined ? { has_issues: query.has_issues } : {}),
		...(query.has_feedback !== undefined
			? { has_feedback: query.has_feedback }
			: {}),
	});

	const programmers = filterUsers?.programmers ?? [];
	const firstReviewers = filterUsers?.first_reviewers ?? [];
	const secondaryReviewers = filterUsers?.secondary_reviewers ?? [];
	const programs = data?.results ?? [];
	const resolvedSearchPlaceholder =
		searchPlaceholder ?? PROGRAM_SCORING_SEARCH_PLACEHOLDERS[programType];

	const resolveProgramId = useCallback(
		async (record: ProgramScoringRow): Promise<string | null> => {
			if (record.id) {
				return record.id;
			}
			try {
				const program = await ensureProgram({
					order_line_item: record.order_line_item.id,
					program_type: programType,
				}).unwrap();
				return program.id;
			} catch {
				message.error('Failed to prepare program');
				return null;
			}
		},
		[ensureProgram, programType]
	);

	const handleViewReview = useCallback(
		async (record: ProgramScoringRow, reviewId: string) => {
			const programId = await resolveProgramId(record);
			if (!programId) {
				return;
			}
			try {
				const review = await fetchReview(reviewId).unwrap();
				if (isReviewAuthor(review, currentUser?.id)) {
					setViewingReview(null);
					setReviewFormModal({ programId, review });
				} else {
					setReviewFormModal(null);
					setViewingReview(review);
				}
			} catch {
				message.error('Failed to load review');
			}
		},
		[currentUser, fetchReview, resolveProgramId]
	);

	const handleLogReview = useCallback(
		async (record: ProgramScoringRow) => {
			const programId = await resolveProgramId(record);
			if (!programId) {
				return;
			}
			setViewingReview(null);
			setReviewFormModal({ programId, review: null });
		},
		[resolveProgramId]
	);

	const handleDeleteReview = useCallback(
		async (record: ProgramScoringRow, reviewId: string) => {
			const programId = await resolveProgramId(record);
			if (!programId) {
				return;
			}
			try {
				await deleteReview({ id: reviewId, programId }).unwrap();
				setReviewFormModal((current) =>
					current?.review?.id === reviewId ? null : current
				);
				setViewingReview((current) =>
					current?.id === reviewId ? null : current
				);
				message.success('Review removed');
			} catch {
				message.error('Failed to remove review');
			}
		},
		[deleteReview, resolveProgramId]
	);

	const handleViewIssues = useCallback(
		async (record: ProgramScoringRow) => {
			const programId = await resolveProgramId(record);
			if (!programId) {
				return;
			}
			setIssuesModal({
				programId,
				partName: programPartLabel(
					record.order_line_item.name || 'Program',
					record.filename
				),
			});
		},
		[resolveProgramId]
	);

	const handleViewFeedback = useCallback(
		async (record: ProgramScoringRow) => {
			const programId = await resolveProgramId(record);
			if (!programId) {
				return;
			}
			setFeedbackModal({
				programId,
				partName: programPartLabel(
					record.order_line_item.name || 'Program',
					record.filename
				),
			});
		},
		[resolveProgramId]
	);

	const handleViewReviews = useCallback(
		async (record: ProgramScoringRow) => {
			const programId = await resolveProgramId(record);
			if (!programId) {
				return;
			}
			setReviewsModal({
				programId,
				partName: programPartLabel(
					record.order_line_item.name || 'Program',
					record.filename
				),
			});
		},
		[resolveProgramId]
	);

	const handleLogIssue = useCallback(
		async (record: ProgramScoringRow) => {
			const programId = await resolveProgramId(record);
			if (!programId) {
				return;
			}
			setIssueModalProgramId(programId);
		},
		[resolveProgramId]
	);

	const handleLogFeedback = useCallback(
		async (record: ProgramScoringRow) => {
			const programId = await resolveProgramId(record);
			if (!programId) {
				return;
			}
			setFeedbackModalProgramId(programId);
		},
		[resolveProgramId]
	);

	const handleLogAdditionalProgram = useCallback(
		(record: ProgramScoringRow) => {
			const existingPrograms =
				programType === 'cam'
					? programs
						.filter(
							(row) =>
								row.id &&
								row.order_line_item.id === record.order_line_item.id
						)
						.map((row) => ({
							id: row.id as string,
							filename: row.filename,
						}))
					: [];

			setAddProgramModal({
				orderLineItemId: record.order_line_item.id,
				orderLineItemName: record.order_line_item.name,
				existingPrograms,
			});
		},
		[programs, programType]
	);

	const handleTableChange = (
		pagination: TablePaginationConfig,
		filters: Record<string, FilterValue | null>,
		sorter: SorterResult<ProgramScoringRow> | SorterResult<ProgramScoringRow>[],
		extra: { action: 'paginate' | 'sort' | 'filter' }
	) => {
		if (extra.action === 'paginate') {
			updateQuery({ page: pagination.current ?? 1 });
			return;
		}

		const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
		let ordering: string | undefined;
		if (activeSorter?.order && activeSorter.columnKey) {
			const field = ORDERING_KEYS[String(activeSorter.columnKey)];
			if (field) {
				ordering =
					activeSorter.order === 'descend' ? `-${field}` : field;
			}
		}

		const firstReview = parseReviewerFilter(filters.first_reviewer);
		const secondaryReview = parseReviewerFilter(filters.secondary_reviewer);
		const progressFilters = parseProgressFilterValues(filters.progress);

		updateQuery({
			page: 1,
			ordering,
			part_name: filters.part?.[0] ? String(filters.part[0]) : undefined,
			programming_status: progressFilters.programming_status,
			first_review_status: progressFilters.first_review_status,
			secondary_review_status: progressFilters.secondary_review_status,
			reviewed: undefined,
			programmer: filters.programmer?.[0]
				? String(filters.programmer[0])
				: undefined,
			first_reviewer: firstReview.reviewer,
			no_first_review: firstReview.none,
			secondary_reviewer: secondaryReview.reviewer,
			no_secondary_review: secondaryReview.none,
			has_issues: parseCountFilter(filters.issues),
			has_feedback: parseCountFilter(filters.feedback),
		});
	};

	const columns = useMemo(
		() =>
			buildColumns({
				programType,
				query,
				programmers,
				firstReviewers,
				secondaryReviewers,
				currentUserId: currentUser?.id,
				onViewIssues: handleViewIssues,
				onViewReviews: handleViewReviews,
				onLogReview: handleLogReview,
				onViewReview: handleViewReview,
				onDeleteReview: handleDeleteReview,
				onLogIssue: handleLogIssue,
				onLogFeedback: handleLogFeedback,
				onViewFeedback: handleViewFeedback,
				onLogProgrammingStats: (record) => setProgrammingStatsModal(record),
				onViewProgrammerNotes: (record) => setViewingProgrammerNotes(record),
				onLogAdditionalProgram: handleLogAdditionalProgram,
				onManageLinkedLineItems: (record) =>
					setLinkedLineItemsModal(record),
			}),
		[
			programType,
			query,
			programmers,
			firstReviewers,
			secondaryReviewers,
			handleViewIssues,
			handleViewReviews,
			handleLogReview,
			handleViewReview,
			handleDeleteReview,
			handleLogIssue,
			handleLogFeedback,
			handleViewFeedback,
			handleLogAdditionalProgram,
			currentUser?.id,
		]
	);

	return (
		<div className="w-full bg-white border-b border-r border-l p-6">
			<div className="cam-grid-toolbar">
				<Input.Search
					placeholder={resolvedSearchPlaceholder}
					allowClear
					defaultValue={query.search}
					style={{ maxWidth: 320 }}
					onSearch={(value) => {
						updateQuery({ search: value, page: 1 });
					}}
				/>
			</div>

			<Table
				className="cam-grid-table"
				dataSource={programs}
				columns={columns}
				rowKey={(record) =>
					record.id ?? `empty-${record.order_line_item.id}`
				}
				size="small"
				loading={isLoading || isFetching}
				onChange={handleTableChange}
				pagination={{
					current: query.page,
					total: data?.count ?? 0,
					pageSize: data?.page_size,
					showSizeChanger: false,
					size: 'small',
				}}
				scroll={{ x: 'max-content', y: 'calc(100vh - 360px)' }}
			/>

			<ProgramIssuesModal
				programId={issuesModal?.programId ?? null}
				partName={issuesModal?.partName ?? 'Program'}
				open={!!issuesModal}
				onClose={() => setIssuesModal(null)}
			/>

			<ProgramFeedbackModal
				programId={feedbackModal?.programId ?? null}
				partName={feedbackModal?.partName ?? 'Program'}
				open={!!feedbackModal}
				onClose={() => setFeedbackModal(null)}
			/>

			<ProgramReviewsModal
				programId={reviewsModal?.programId ?? null}
				partName={reviewsModal?.partName ?? 'Program'}
				open={!!reviewsModal}
				onClose={() => setReviewsModal(null)}
			/>

			<IssueFormModal
				programId={issueModalProgramId ?? ''}
				issue={null}
				open={!!issueModalProgramId}
				onClose={() => setIssueModalProgramId(null)}
			/>

			<FeedbackFormModal
				programId={feedbackModalProgramId ?? ''}
				entry={null}
				open={!!feedbackModalProgramId}
				onClose={() => setFeedbackModalProgramId(null)}
			/>

			<ProgrammingStatsFormModal
				open={!!addProgramModal}
				onClose={() => setAddProgramModal(null)}
				programType={programType}
				orderLineItemId={addProgramModal?.orderLineItemId ?? ''}
				orderLineItemName={addProgramModal?.orderLineItemName ?? null}
				existingPrograms={addProgramModal?.existingPrograms ?? []}
			/>

			<ProgrammingStatsFormModal
				open={!!programmingStatsModal}
				onClose={() => setProgrammingStatsModal(null)}
				programType={programType}
				orderLineItemId={programmingStatsModal?.order_line_item.id ?? ''}
				orderLineItemName={programmingStatsModal?.order_line_item.name ?? null}
				programId={programmingStatsModal?.id}
				initialProgrammerId={programmingStatsModal?.programmer?.id ?? null}
				pinnedProgrammer={programmingStatsModal?.programmer ?? null}
				initialFilename={programmingStatsModal?.filename?.trim() || null}
				initialDurationMinutes={
					programmingStatsModal?.programming_duration_minutes ?? null
				}
				initialStartedAt={
					programmingStatsModal?.programming_started_at ?? null
				}
				initialCompletedAt={programmingStatsModal?.programming_completed_at ?? null}
				initialProgrammerFeedback={
					programmingStatsModal?.programmer_feedback ?? null
				}
				existingPrograms={
					programmingStatsModal?.id
						? programs
							.filter(
								(row) =>
									row.id &&
									row.id !== programmingStatsModal.id &&
									row.order_line_item.id ===
									programmingStatsModal.order_line_item.id
							)
							.map((row) => ({
								id: row.id as string,
								filename: row.filename,
							}))
						: []
				}
			/>

			<ReviewFormModal
				key={
					reviewFormModal
						? `${reviewFormModal.programId}-${reviewFormModal.review?.id ?? 'create'}`
						: 'closed'
				}
				programId={reviewFormModal?.programId ?? ''}
				review={reviewFormModal?.review ?? null}
				open={!!reviewFormModal}
				allowReviewerOverride={false}
				onClose={() => setReviewFormModal(null)}
			/>

			<ContentModal
				title={`Programmer notes — ${programPartLabel(
					viewingProgrammerNotes?.order_line_item.name ?? 'Program',
					viewingProgrammerNotes?.filename
				)}`}
				meta={
					viewingProgrammerNotes && (
						<Text type="secondary">
							{authorName(viewingProgrammerNotes.programmer, 'Unassigned')}
						</Text>
					)
				}
				html={viewingProgrammerNotes?.programmer_feedback ?? null}
				emptyText="No notes"
				open={!!viewingProgrammerNotes}
				onClose={() => setViewingProgrammerNotes(null)}
			/>

			<LinkedLineItemsModal
				programId={linkedLineItemsModal?.id ?? null}
				programType={programType}
				linkedLineItems={linkedLineItemsModal?.order_line_items ?? []}
				open={!!linkedLineItemsModal}
				onClose={() => setLinkedLineItemsModal(null)}
			/>

			<ContentModal
				title={`Review by ${authorName(
					viewingReview?.reviewer ?? null,
					'Unassigned'
				)}`}
				meta={
					viewingReview && (
						<>
							{viewingReview.review_duration_minutes != null && (
								<Text type="secondary" className="cam-entry__chip">
									<ClockCircleOutlined />{' '}
									{viewingReview.review_duration_minutes} min
								</Text>
							)}
							{viewingReview.review_completed_at && (
								<Text type="secondary">
									Completed{' '}
									{dayjs(viewingReview.review_completed_at).format(
										'MMM D, YYYY [at] h:mm A'
									)}
								</Text>
							)}
						</>
					)
				}
				html={viewingReview?.feedback ?? null}
				emptyText="No feedback"
				open={!!viewingReview}
				onClose={() => setViewingReview(null)}
			/>
		</div>
	);
};

export default ProgramScoringTable;
